import { useState, useEffect } from 'react';
import { User, Mail, Phone, MapPin, Calendar, Truck, Clock, Briefcase, Check, Trash2, Eye, X, ChevronDown, ChevronUp, UserPlus, XCircle } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const AdminCandidatures = ({ user, activeModule = "b2b" }) => {
  const [candidatures, setCandidatures] = useState([]);
  const [approvedCandidatures, setApprovedCandidatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('pending'); // 'pending' or 'approved'

  useEffect(() => {
    fetchCandidatures();
  }, []);

  const fetchCandidatures = async () => {
    try {
      // Fetch pending candidatures
      const pendingRes = await axios.get(`${API}/candidatures?status=pending`, { withCredentials: true });
      setCandidatures(pendingRes.data);
      
      // Fetch approved candidatures
      const approvedRes = await axios.get(`${API}/candidatures?status=approved`, { withCredentials: true });
      setApprovedCandidatures(approvedRes.data);
    } catch (error) {
      console.error('Failed to fetch candidatures:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (candidatureId) => {
    if (!window.confirm('Approuver cette candidature ? Elle sera déplacée vers l\'onglet "Candidatures approuvées".')) return;
    
    setProcessing(candidatureId);
    try {
      await axios.post(`${API}/candidatures/${candidatureId}/approve`, {}, { withCredentials: true });
      setMessage({ type: 'success', text: '✅ Candidature approuvée et déplacée vers l\'onglet "Candidatures approuvées".' });
      await fetchCandidatures();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erreur lors de l\'approbation' });
    } finally {
      setProcessing(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const handleReject = async (candidatureId) => {
    if (!window.confirm('Refuser cette candidature ?')) return;
    
    setProcessing(candidatureId);
    try {
      await axios.post(`${API}/candidatures/${candidatureId}/reject`, {}, { withCredentials: true });
      setMessage({ type: 'success', text: '✅ Candidature refusée.' });
      await fetchCandidatures();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erreur lors du refus' });
    } finally {
      setProcessing(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const handleCreateAccount = async (candidatureId) => {
    if (!window.confirm('Créer un compte employé pour cette candidature ? Un mot de passe temporaire sera généré.')) return;
    
    setProcessing(candidatureId);
    try {
      const response = await axios.post(`${API}/candidatures/${candidatureId}/create-account`, {}, { withCredentials: true });
      setMessage({ 
        type: 'success', 
        text: `✅ Compte créé ! Email: ${response.data.email} | Mot de passe temporaire: ${response.data.temp_password}`
      });
      await fetchCandidatures();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erreur lors de la création du compte' });
    } finally {
      setProcessing(null);
      // Keep message longer for account creation
      setTimeout(() => setMessage({ type: '', text: '' }), 30000);
    }
  };

  const handleDelete = async (candidatureId) => {
    if (!window.confirm('Supprimer définitivement cette candidature ?')) return;
    
    setProcessing(candidatureId);
    try {
      await axios.delete(`${API}/candidatures/${candidatureId}`, { withCredentials: true });
      setMessage({ type: 'success', text: '✅ Candidature supprimée.' });
      await fetchCandidatures();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erreur lors de la suppression' });
    } finally {
      setProcessing(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const renderCandidatureCard = (candidature, isApproved = false) => (
    <Card key={candidature.candidature_id} className={`${isApproved ? 'border-green-200' : 'border-blue-200'} hover:shadow-lg transition-shadow`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-3">
            <div className={`w-12 h-12 ${isApproved ? 'bg-green-100' : 'bg-blue-100'} rounded-full flex items-center justify-center`}>
              <User className={`w-6 h-6 ${isApproved ? 'text-green-600' : 'text-blue-600'}`} />
            </div>
            <div>
              <span className="text-lg">{candidature.last_name}, {candidature.first_name}</span>
              <p className="text-sm text-gray-500 font-normal">{candidature.email}</p>
            </div>
          </CardTitle>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">
              Reçue le {formatDate(candidature.created_at)}
            </span>
            {isApproved && (
              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                Approuvée
              </span>
            )}
            <button
              onClick={() => setExpandedId(expandedId === candidature.candidature_id ? null : candidature.candidature_id)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              {expandedId === candidature.candidature_id ? (
                <ChevronUp className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              )}
            </button>
          </div>
        </div>
      </CardHeader>

      {/* Expanded Content */}
      {expandedId === candidature.candidature_id && (
        <CardContent className="pt-0">
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Personal Info */}
            <div className="space-y-3">
              <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                <User className="w-4 h-4" />
                Informations personnelles
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <Mail className="w-4 h-4 text-gray-400" />
                  {candidature.email}
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Phone className="w-4 h-4 text-gray-400" />
                  {candidature.phone}
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  {candidature.address}
                  {candidature.postal_code && (
                    <span className="px-2 py-0.5 bg-gray-100 rounded text-gray-700 font-medium">
                      {candidature.postal_code}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  Né(e) le {formatDate(candidature.birth_date)}
                </div>
              </div>
            </div>

            {/* Professional Info */}
            <div className="space-y-3">
              <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                <Briefcase className="w-4 h-4" />
                Informations professionnelles
              </h4>
              <div className="space-y-2 text-sm">
                {candidature.license_years && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Clock className="w-4 h-4 text-gray-400" />
                    {candidature.license_years} ans de permis
                  </div>
                )}
                <div className="flex items-center gap-2 text-gray-600">
                  <Truck className="w-4 h-4 text-gray-400" />
                  {candidature.has_vehicle ? (
                    <span>Possède un véhicule: {candidature.vehicle_type || 'Type non précisé'}</span>
                  ) : (
                    <span>Ne possède pas de véhicule</span>
                  )}
                </div>
              </div>
              {candidature.experience && (
                <div className="mt-3">
                  <p className="text-sm font-medium text-gray-700 mb-1">Expérience:</p>
                  <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                    {candidature.experience}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            {isApproved ? (
              <>
                <Button
                  onClick={() => handleCreateAccount(candidature.candidature_id)}
                  disabled={processing === candidature.candidature_id}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  {processing === candidature.candidature_id ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Créer le compte employé
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => handleDelete(candidature.candidature_id)}
                  disabled={processing === candidature.candidature_id}
                  variant="outline"
                  className="border-red-300 text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => handleApprove(candidature.candidature_id)}
                  disabled={processing === candidature.candidature_id}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  {processing === candidature.candidature_id ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Approuver
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => handleReject(candidature.candidature_id)}
                  disabled={processing === candidature.candidature_id}
                  variant="outline"
                  className="flex-1 border-orange-300 text-orange-600 hover:bg-orange-50"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Refuser
                </Button>
                <Button
                  onClick={() => handleDelete(candidature.candidature_id)}
                  disabled={processing === candidature.candidature_id}
                  variant="outline"
                  className="border-red-300 text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </CardContent>
      )}

      {/* Collapsed Actions */}
      {expandedId !== candidature.candidature_id && (
        <CardContent className="pt-0">
          <div className="flex gap-3">
            {isApproved ? (
              <>
                <Button
                  onClick={() => handleCreateAccount(candidature.candidature_id)}
                  disabled={processing === candidature.candidature_id}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <UserPlus className="w-4 h-4 mr-1" />
                  Créer compte
                </Button>
                <Button
                  onClick={() => setExpandedId(candidature.candidature_id)}
                  size="sm"
                  variant="outline"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  Détails
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => handleApprove(candidature.candidature_id)}
                  disabled={processing === candidature.candidature_id}
                  size="sm"
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Check className="w-4 h-4 mr-1" />
                  Approuver
                </Button>
                <Button
                  onClick={() => setExpandedId(candidature.candidature_id)}
                  size="sm"
                  variant="outline"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  Détails
                </Button>
                <Button
                  onClick={() => handleDelete(candidature.candidature_id)}
                  disabled={processing === candidature.candidature_id}
                  size="sm"
                  variant="outline"
                  className="border-red-300 text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );

  if (loading) {
    return (
      <Layout user={user} activeModule={activeModule}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F172A]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} activeModule={activeModule}>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-[#0F172A] font-['Chivo']">Candidatures</h1>
          <p className="text-gray-500 mt-1">
            {candidatures.length} en attente • {approvedCandidatures.length} approuvée(s)
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b">
          <button
            onClick={() => setActiveTab('pending')}
            className={`px-4 py-3 font-medium text-sm transition-colors relative ${
              activeTab === 'pending'
                ? 'text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="flex items-center gap-2">
              📥 En attente
              {candidatures.length > 0 && (
                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-bold rounded-full">
                  {candidatures.length}
                </span>
              )}
            </span>
            {activeTab === 'pending' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('approved')}
            className={`px-4 py-3 font-medium text-sm transition-colors relative ${
              activeTab === 'approved'
                ? 'text-green-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="flex items-center gap-2">
              ✅ Candidatures approuvées
              {approvedCandidatures.length > 0 && (
                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded-full">
                  {approvedCandidatures.length}
                </span>
              )}
            </span>
            {activeTab === 'approved' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-green-600" />
            )}
          </button>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
            {message.text}
          </div>
        )}

        {/* Content based on active tab */}
        {activeTab === 'pending' ? (
          candidatures.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">Aucune candidature en attente</p>
                <p className="text-gray-400 text-sm mt-2">Les nouvelles candidatures apparaîtront ici</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {candidatures.map((candidature) => renderCandidatureCard(candidature, false))}
            </div>
          )
        ) : (
          approvedCandidatures.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Check className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">Aucune candidature approuvée</p>
                <p className="text-gray-400 text-sm mt-2">Les candidatures approuvées apparaîtront ici</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-700">
                  💡 <strong>Info:</strong> Ces candidatures ont été approuvées mais n'ont pas encore de compte employé. 
                  Cliquez sur "Créer compte" pour générer un compte avec un mot de passe temporaire.
                </p>
              </div>
              {approvedCandidatures.map((candidature) => renderCandidatureCard(candidature, true))}
            </div>
          )
        )}
      </div>
    </Layout>
  );
};

export default AdminCandidatures;
